export interface PromotionClickTarget {
    click_type: string
    click_text: string
}

const MAX_TEXT_LENGTH = 120
const ACTIONABLE_SELECTOR = 'a, button, label, [role="button"], .btn'
// optional explicit annotations for a semantic type/text a heuristic can't infer (e.g. "copy_voucher_code", "info")
const TYPE_ATTR = 'data-promo-click-type'
const TEXT_ATTR = 'data-promo-click-text'

function resolveType(el: Element): string {
    const tag = el.tagName.toLowerCase()
    // button styling wins over the anchor tag, so a button-styled link (<a class="btn">) reads as a CTA rather than a nav link
    if (tag === 'button' || el.getAttribute('role') === 'button' || el.classList.contains('btn')) return 'button'
    if (tag === 'a') return 'link'
    if (tag === 'img' || tag === 'picture') return 'image'
    if (tag === 'label') return 'label'
    return tag || 'element'
}

function resolveText(el: Element): string {
    const label = el.getAttribute('aria-label')?.trim() || el.getAttribute('title')?.trim()
    if (label) return label.slice(0, MAX_TEXT_LENGTH)
    // innerText ignores non-rendered content (<style>/<script>, SVG internals); textContent is only the SVG fallback
    const raw = (el as HTMLElement).innerText ?? el.textContent ?? ''
    // collapse any run of whitespace (newlines/tabs/multi-space from markup indentation) into a single space
    const text = raw.replace(/\s+/g, ' ').trim()
    if (text) return text.slice(0, MAX_TEXT_LENGTH)
    const alt = el.querySelector('img')?.getAttribute('alt')?.trim() ?? ''
    return alt.slice(0, MAX_TEXT_LENGTH)
}

/**
 * Derives element-agnostic click_type / click_text from a click source by resolving the nearest
 * actionable ancestor of the clicked node (bounded by currentTarget, SVG targets included).
 */
export function resolvePromotionClickTarget(source: MouseEvent | HTMLElement | null | undefined): PromotionClickTarget | undefined {
    // actually clicked element
    const start = source instanceof MouseEvent ? source.target : source
    // element with click event handler 
    const boundary = source instanceof MouseEvent && source.currentTarget instanceof HTMLElement ? source.currentTarget : null

    // filter non DOM nodes like text
    const startEl = start instanceof Element ? start : null
    // closest() lives on Element, so it also works for SVG targets and crosses the SVG->HTML boundary going up
    const found = startEl?.closest(ACTIONABLE_SELECTOR) ?? null
    // use `found` if it exists AND (there is no boundary OR the boundary contains it); else fall back to the boundary (wrapper), else to startEl
    // the `!boundary` case matters when called with a bare HTMLElement (no event): keep the actionable ancestor instead of the raw start node
    const resolved = found && (!boundary || boundary.contains(found)) ? found : (boundary ?? startEl)
    if (!resolved) return undefined

    return {
        click_type: annotation(startEl, boundary, TYPE_ATTR) ?? resolveType(resolved),
        click_text: annotation(startEl, boundary, TEXT_ATTR) ?? resolveText(resolved),
    }
}

/** Nearest explicit annotation on/above the clicked node, bounded by currentTarget. */
function annotation(startEl: Element | null, boundary: HTMLElement | null, attr: string): string | null {
    const el = startEl?.closest(`[${attr}]`)
    if (!el || (boundary && !boundary.contains(el))) return null
    const value = el.getAttribute(attr)?.trim()
    return value || null
}
